import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import _ from 'lodash'
import queryString from 'query-string'
import {
  Axios,
  Grid,
  Segment,
  DataTable,
  Table,
  Checkbox,
  ConditionWrapper,
  BaseForm as Form,
  Text,
} from 'component/base'

export const SeikyuDetail = withRouter(props => {
  const {
    history,
    enableQueryCondition,
    multiple,
    selectedId,
    onSelectChange,
    refresh,
    kumiaihito_seikyu_id,
  } = props

  const query = useMemo(
    () =>
      queryString.parse(window.location.search, {
        parseNumbers: true,
        parseBooleans: true,
      }),
    [window.location.search]
  )

  const path = useMemo(() => window.location.pathname, [
    window.location.pathname,
  ])

  const defaultCondition = useMemo(() => {
    return {}
  }, [])

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [condition, setCondition] = useState(
    _.assign({}, defaultCondition, enableQueryCondition ? query : {})
  )

  useEffect(() => {
    setSelectedIds(
      multiple
        ? selectedId && _.isArray(selectedId)
          ? selectedId
          : []
        : selectedId
        ? [selectedId]
        : []
    )
  }, [selectedId])

  useEffect(() => {
    onSelectChange(
      selectedIds,
      _.filter(data, row =>
        _.find(selectedIds, id => row.kumiaihito_seikyu_meisai_id === id)
      )
    )
  }, [selectedIds])

  useEffect(() => {
    setCondition(condition =>
      enableQueryCondition
        ? _(condition).assign(query).value()
        : _(condition).value()
    )
  }, [defaultCondition, enableQueryCondition, query])

  useEffect(() => {
    callApi(condition)
    setSelectedIds([])
  }, [condition, refresh])

  const handleConditionChange = (condition, isOnClear) => {
    setCondition(isOnClear ? defaultCondition : condition)
    if (enableQueryCondition)
      isOnClear
        ? history.push(`${path}`)
        : history.push(`${path}?${queryString.stringify(condition)}`)
  }

  const callApi = async condition => {
    setLoading(true)
    const { data, error } = await Axios.ajax.get(
      `/api/staff/kumiaiin/kumiaihito_seikyu/${kumiaihito_seikyu_id}/kumiaihito_seikyu_meisai`,
      {
        params: {
          ...condition,
        },
      }
    )
    setLoading(false)
    if (error) return
    setData(data)
  }

  const handleChangeSelect = (selectedId, selected) => {
    setSelectedIds(selectedIds =>
      selected
        ? multiple
          ? _.union(selectedIds, [selectedId])
          : [selectedId]
        : _.filter(selectedIds, id => id !== selectedId)
    )
  }

  const TableRowWithProps = useCallback(
    props => (
      <TableRow
        {...props}
        history={history}
        handleChangeSelect={handleChangeSelect}
      />
    ),
    [history, handleChangeSelect]
  )

  return (
    <>
      <Segment basic loading={loading} style={{ padding: '0em' }}>
        <ConditionWrapper
          defaultCondition={defaultCondition}
          condition={condition}
          onConditionChange={handleConditionChange}
          FormElement={SearchElement}
          enableSidebar={false}
          compact
        >
          <DataTable
            name='kumiaiin-kumiaihi-seikyu-detail'
            sortable
            filterable
            tableHeight='50vh'
            tableHeader={TableHeader}
            tableRow={TableRowWithProps}
            data={data}
            bottomControl={<></>}
            selectedIds={selectedIds}
          />
        </ConditionWrapper>
      </Segment>
    </>
  )
})

SeikyuDetail.propTypes = {
  enableQueryCondition: PropTypes.bool,
  multiple: PropTypes.bool,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onSelectChange: PropTypes.func,
}

SeikyuDetail.defaultProps = {
  enableQueryCondition: false,
  multiple: false,
  selectedValue: '',
  onSelectChange: () => {},
}

const TableHeader = props => {
  const { withOrder, handleSort, column, direction } = props

  return withOrder(
    <Table.Row>
      <Table.HeaderCell style={{ width: '3.0rem' }} content='#' />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'kumiaiin_cd' ? direction : null}
        onClick={() => handleSort('kumiaiin_cd')}
        content='組合員コード'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'kumiaiin_name' ? direction : null}
        onClick={() => handleSort('kumiaiin_name')}
        content='組合員名'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'unsoten_name' ? direction : null}
        onClick={() => handleSort('unsoten_name')}
        content='運送店名'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'kinyukikan_name' ? direction : null}
        onClick={() => handleSort('kinyukikan_name')}
        content='仕向金融機関'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'taisho_gaku' ? direction : null}
        onClick={() => handleSort('taisho_gaku')}
        content='対象金額'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'chosei_gaku' ? direction : null}
        onClick={() => handleSort('chosei_gaku')}
        content='調整額'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'azukarikin_chosei_gaku' ? direction : null}
        onClick={() => handleSort('azukarikin_chosei_gaku')}
        content='預り金調整額'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'azukarikin_zan_gaku' ? direction : null}
        onClick={() => handleSort('azukarikin_zan_gaku')}
        content='預り金残額'
      />
      <Table.HeaderCell
        style={{ width: '20.0rem' }}
        sorted={column === 'chosei_riyu' ? direction : null}
        onClick={() => handleSort('chosei_riyu')}
        content='調整理由'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'seikyu_gaku' ? direction : null}
        onClick={() => handleSort('seikyu_gaku')}
        content='請求額'
      />
    </Table.Row>
  )
}

const TableRow = props => {
  const { withOrder, data, selectedIds, handleChangeSelect } = props || {}

  const {
    kumiaihito_seikyu_meisai_id: id,
    kumiaiin_cd,
    kumiaiin_name,
    unsoten_name,
    kinyukikan_name,
    taisho_gaku,
    azukarikin_zan_gaku,
    chosei_gaku,
    azukarikin_chosei_gaku,
    chosei_riyu,
    seikyu_gaku,
  } = data || {}

  const [isSelected, setIsSelected] = useState(
    selectedIds.indexOf(id) >= 0 ? true : false
  )

  const checkboxChange = (event, { value, checked }) => {
    event.stopPropagation()
    setIsSelected(checked)
    handleChangeSelect(value, checked)
  }

  return withOrder(
    <Table.Row
      positive={isSelected}
      onClick={() => {
        setIsSelected(state => !state)
        handleChangeSelect(id, !isSelected)
      }}
    >
      <Table.Cell
        style={{ width: '3.0rem' }}
        textAlign='center'
        content={
          <Checkbox checked={isSelected} onChange={checkboxChange} value={id} />
        }
      />
      <Table.Cell style={{ width: '10.0rem' }} content={kumiaiin_cd} />
      <Table.Cell style={{ width: '15.0rem' }} content={kumiaiin_name} />
      <Table.Cell style={{ width: '15.0rem' }} content={unsoten_name} />
      <Table.Cell style={{ width: '15.0rem' }} content={kinyukikan_name} />
      <Table.Cell
        style={{
          width: '15.0rem',
          textAlign: 'right',
        }}
        content={<Text.Comma content={taisho_gaku} />}
      />
      <Table.Cell
        style={{
          width: '15.0rem',
          textAlign: 'right',
        }}
        content={<Text.Comma content={chosei_gaku} />}
      />
      <Table.Cell
        style={{
          width: '15.0rem',
          textAlign: 'right',
        }}
        content={<Text.Comma content={azukarikin_chosei_gaku} />}
      />
      <Table.Cell
        style={{
          width: '15.0rem',
          textAlign: 'right',
        }}
        content={<Text.Comma content={azukarikin_zan_gaku} />}
      />
      <Table.Cell style={{ width: '20.0rem' }} content={chosei_riyu} />
      <Table.Cell
        style={{
          width: '15.0rem',
          textAlign: 'right',
        }}
        content={<Text.Comma content={seikyu_gaku} />}
      />
    </Table.Row>
  )
}

const SearchElement = props => {
  const { nav, condition } = props

  return (
    <>
      <Grid.Column
        style={{
          display: !nav || condition.kumiaiin_name ? 'block' : 'none',
        }}
      >
        <Form.Textbox.Standard
          value={condition.kumiaiin_name}
          label='組合員名'
          name='kumiaiin_name'
        />
      </Grid.Column>
      <Grid.Column
        style={{
          display: !nav || condition.unsoten_name ? 'block' : 'none',
        }}
      >
        <Form.Textbox.Standard
          value={condition.unsoten_name}
          label='運送店名'
          name='unsoten_name'
        />
      </Grid.Column>
    </>
  )
}
